import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Restore as RestoreIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../../../contexts/AuthContext';
import ServerDataTable from '../../ServerDataTable/ServerDataTable';
import api from '../../../services/api';
import ConfirmDialog from '../../ConfirmDialog';
import StatusBadge from '../../StatusBadge';

export default function FieldMappingTab() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const navigate = useNavigate();
  const location = useLocation();
  const DEFAULT_PAGE_SIZE = 10;
  // Fetch sync status
  const {
    data: syncStatusResponse,
    isLoading: syncStatusLoading
  } = useQuery({
    queryKey: ['customerFieldMappingsSyncStatus'],
    queryFn: async () => {
      const response = await api.get('/customer-sync/status');
      return response.data;
    },
    refetchOnWindowFocus: true,
    refetchInterval: 5000, // Poll every 5 seconds
    staleTime: 0
  });

  const isSyncRunning = syncStatusResponse?.data?.isRunning || false;

  // State for ServerDataTable
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortConfig, setSortConfig] = useState({
    key: '',
    direction: ''
  });

  // State for field mappings
  const [editingId, setEditingId] = useState(null);
  const [editedSourceField, setEditedSourceField] = useState('');
  const [editedTargetField, setEditedTargetField] = useState('');
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newSourceField, setNewSourceField] = useState('');
  const [newTargetField, setNewTargetField] = useState('');
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [sourceFieldError, setSourceFieldError] = useState('');
  const [targetFieldError, setTargetFieldError] = useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [isAddingSaving, setIsAddingSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Fetch field mappings
  const {
    data: mappingsResponse,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery({
    queryKey: ['customerFieldMappings', 'erp-to-shopify', debouncedSearchTerm, pageIndex, pageSize],
    queryFn: async () => {
      const response = await api.get('/customer-sync/field-mappings', {
        params: {
          search: debouncedSearchTerm || '',
          limit: pageSize,
          offset: pageIndex * pageSize
        }
      });
      return response.data;
    }
  });

  // Extract data and pagination info
  const mappingsData = mappingsResponse?.data || [];
  const totalCount = mappingsResponse?.pagination?.total || 0;

  // Prepare data for table (add new row if adding)
  const tableData = isAddingNew ? [
    {
      id: 'new',
      source_field: newSourceField,
      target_field: newTargetField,
      isNew: true
    },
    ...(mappingsData || [])
  ] : (mappingsData || []);

  const paginatedData = tableData;

  const adjustedTotalCount = isAddingNew ? totalCount + 1 : totalCount;

  const columns = [
    {
      field: 'serialNumber',
      headerName: 'S.No.',
      width: 80,
      sortable: false,
      renderCell: ({ rowIndex }) => {
        const row = tableData[rowIndex];
        if (row?.id === 'new') return 'New';
        return rowIndex + 1 + (pageIndex * pageSize);
      }
    },
    {
      field: 'source_field',
      headerName: 'ERP Source Field',
      width: 250,
      sortable: true,
      renderCell: ({ value, row }) => {
        if (row.id === 'new') {
          return (
            <TextField
              fullWidth
              size='small'
              placeholder='Enter Source Field'
              value={newSourceField}
              onChange={(e) => setNewSourceField(e.target.value)}
              error={!!sourceFieldError}
              helperText={sourceFieldError}
              disabled={Boolean(isRefreshing) || isSyncRunning}
            />
          );
        }
        if (editingId === row.id) {
          return (
            <TextField
              fullWidth
              size='small'
              value={editedSourceField}
              onChange={(e) => setEditedSourceField(e.target.value)}
              error={!!sourceFieldError}
              helperText={sourceFieldError}
              disabled={Boolean(isRefreshing) || isSyncRunning}
            />
          );
        }
        return value;
      }
    },
    {
      field: 'target_field',
      headerName: 'Shopify Target Field',
      width: 250,
      sortable: true,
      renderCell: ({ value, row }) => {
        if (row.id === 'new') {
          return (
            <TextField
              fullWidth
              size='small'
              placeholder='Enter Target Field'
              value={newTargetField}
              onChange={(e) => setNewTargetField(e.target.value)}
              error={!!targetFieldError}
              helperText={targetFieldError}
              disabled={Boolean(isRefreshing) || isSyncRunning}
            />
          );
        }
        if (editingId === row.id) {
          return (
            <TextField
              fullWidth
              size='small'
              value={editedTargetField}
              onChange={(e) => setEditedTargetField(e.target.value)}
              error={!!targetFieldError}
              helperText={targetFieldError}
              disabled={Boolean(isRefreshing) || row.is_required || isSyncRunning}
              InputProps={{
                startAdornment: row.is_required ? (
                  <LockIcon fontSize='small' color='action' sx={{ mr: 1 }} />
                ) : null
              }}
            />
          );
        }
        return (
          <Box display='flex' alignItems='center' gap={1}>
            {value}
            {row.is_required ? <LockIcon fontSize='small' color='action' /> : null}
          </Box>
        );
      }
    },
    {
      field: 'is_required',
      headerName: 'Required',
      width: 100,
      sortable: true,
      renderCell: ({ value }) => {
        return (
          <StatusBadge customLabel={value ? 'Yes' : 'No'} customColor={value ? 'error' : 'success'} customize={true} />
        );
      }
    }
  ];

  if (isAdmin) {
    columns.push({
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: ({ row }) => {
        if (row.id === 'new') {
          return (
            <Box display='flex' gap={1}>
              <Tooltip title='Save'>
                <span>
                  <IconButton
                    size='small'
                    color='primary'
                    onClick={handleSaveNew}
                    disabled={Boolean(isAddingSaving) || Boolean(isRefreshing) || isSyncRunning || Boolean(isMutating)}
                  >
                    {isAddingSaving ? <CircularProgress size={20} /> : <SaveIcon />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title='Cancel'>
                <span>
                  <IconButton
                    size='small'
                    color='default'
                    onClick={handleCancelAdd}
                    disabled={Boolean(isAddingSaving) || Boolean(isRefreshing) || isSyncRunning || Boolean(isMutating)}
                  >
                    <CancelIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        }
        if (editingId === row.id) {
          return (
            <Box display='flex' gap={1}>
              <Tooltip title='Save'>
                <span>
                  <IconButton
                    size='small'
                    color='primary'
                    onClick={handleSaveEdit}
                    disabled={Boolean(savingId === row.id) || Boolean(isRefreshing) || isSyncRunning || Boolean(isMutating)}
                  >
                    {savingId === row.id ? <CircularProgress size={20} /> : <SaveIcon />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title='Cancel'>
                <span>
                  <IconButton
                    size='small'
                    color='default'
                    onClick={handleCancelEdit}
                    disabled={Boolean(savingId === row.id) || Boolean(isRefreshing) || isSyncRunning || Boolean(isMutating)}
                  >
                    <CancelIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        }
        return (
          <Box display='flex' gap={1}>
            <Tooltip title='Edit'>
              <span>
                <IconButton
                  size='small'
                  color='primary'
                  onClick={() => handleEditClick(row)}
                  disabled={!isAdmin || Boolean(isAddingNew) || Boolean(editingId) || Boolean(deletingId === row.id) || Boolean(isRefreshing) || isSyncRunning || Boolean(isMutating)}
                >
                  <EditIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={row.is_required ? 'Cannot delete required field mapping' : 'Delete'}>
              <span>
                <IconButton
                  size='small'
                  color='error'
                  onClick={() => handleDelete(row.id)}
                  disabled={!isAdmin || Boolean(isAddingNew) || Boolean(editingId) || Boolean(deletingId === row.id) || Boolean(isRefreshing) || Boolean(row.is_required) || isSyncRunning || Boolean(isMutating)}
                >
                  {deletingId === row.id ? <CircularProgress size={20} /> : <DeleteIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      }
    });
  }

  // Update field mappings
  const updateMappingsMutation = useMutation({
    mutationFn: async (updatedMappings) => {
      const response = await api.updateCustomerFieldMappings(updatedMappings);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Field mappings updated successfully');
      queryClient.invalidateQueries({ queryKey: ['customerFieldMappings', 'erp-to-shopify'] });
    },
    onError: (error) => {
      if (error.response?.status === 423) {
        toast.error(error.response.data.message);
      } else {
        toast.error(`Failed to update field mappings: ${error.response?.data?.message || error.message}`);
      }
    }
  });

  // Update a single field mapping
  const updateSingleMappingMutation = useMutation({
    mutationFn: async ({ id, mapping }) => {
      const response = await api.updateSingleCustomerFieldMapping(id, mapping);
      return response.data;
    },
    onMutate: async ({ id }) => {
      // Set saving ID when mutation starts
      setSavingId(id);
    },
    onSuccess: () => {
      toast.success('Field mapping updated successfully');
      queryClient.invalidateQueries({ queryKey: ['customerFieldMappings', 'erp-to-shopify'] });
      setSavingId(null);
      setEditingId(null);
      setEditedSourceField('');
      setEditedTargetField('');
    },
    onError: (error) => {
      if (error.response?.status === 423) {
        toast.error(error.response.data.message);
      } else {
        toast.error(`Failed to update field mapping: ${error.response?.data?.message || error.message}`);
      }
      setSavingId(null);
    }
  });

  // Create a new field mapping
  const createMappingMutation = useMutation({
    mutationFn: async (mapping) => {
      const response = await api.createCustomerFieldMapping(mapping);
      return response.data;
    },
    onMutate: async () => {
      // Set adding saving state when mutation starts
      setIsAddingSaving(true);
    },
    onSuccess: () => {
      toast.success('Field mapping created successfully');
      queryClient.invalidateQueries({ queryKey: ['customerFieldMappings', 'erp-to-shopify'] });
      setIsAddingSaving(false);
      setIsAddingNew(false);
      setNewSourceField('');
      setNewTargetField('');
    },
    onError: (error) => {
      if (error.response?.status === 423) {
        toast.error(error.response.data.message);
      } else {
        toast.error(`Failed to create field mapping: ${error.response?.data?.message || error.message}`);
      }
      setIsAddingSaving(false);
    }
  });

  // Delete a single field mapping
  const deleteMappingMutation = useMutation({
    mutationFn: async (id) => {
      // Use a dedicated DELETE endpoint for removing a specific mapping
      const response = await api.deleteCustomerFieldMapping(id);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Field mapping deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['customerFieldMappings', 'erp-to-shopify'] });
    },
    onError: (error) => {
      if (error.response?.status === 423) {
        toast.error(error.response.data.message);
      } else {
        toast.error(`Failed to delete field mapping: ${error.response?.data?.message || error.message}`);
      }
    },
    onSettled: () => {
      setDeletingId(null);
    }
  });

  // Reset field mappings
  const resetMappingsMutation = useMutation({
    mutationFn: async () => {
      const response = await api.resetCustomerFieldMappingsViaQueue();
      return response.data;
    },
    onSuccess: () => {
      toast.success('Field mapping reset job has been queued successfully');
      queryClient.invalidateQueries({ queryKey: ['customerFieldMappings', 'erp-to-shopify'] });
      setConfirmResetOpen(false);
    },
    onError: (error) => {
      if (error.response?.status === 423) {
        toast.error(error.response.data.message);
      } else {
        toast.error(`Failed to queue field mapping reset job: ${error.response?.data?.message || error.message}`);
      }
      setConfirmResetOpen(false);
    }
  });

  // Validate source field selection
  const validateSourceField = (value) => {
    if (!value) {
      setSourceFieldError('Source field is required');
      return false;
    }

    setSourceFieldError('');
    return true;
  };

  // Validate target field selection
  const validateTargetField = (value, currentMapping = null) => {
    if (!value) {
      setTargetFieldError('Target field is required');
      return false;
    }

    const isDuplicate = mappingsData.some(mapping =>
      mapping.target_field === value && mapping.id !== (currentMapping?.id || null)
    );

    if (isDuplicate) {
      setTargetFieldError('This target field is already mapped');
      return false;
    }

    setTargetFieldError('');
    return true;
  };

  const updateURLWithParams = (currentPage, currentPageSize, currentSearchTerm) => {
    const params = new URLSearchParams(location.search);

    // Add pagination parameters to URL
    if (currentPage >= 0) {
      // Convert 0-indexed internal state to 1-indexed URL
      params.set('page', (currentPage + 1).toString());
    }

    params.set('pageSize', currentPageSize.toString() || DEFAULT_PAGE_SIZE.toString());

    // Handle search term - add if not empty, remove if empty
    params.set('search', currentSearchTerm || '');

    // Update URL without reloading the page
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pageParam = params.get('page');
    const pageSizeParam = params.get('pageSize');
    const searchParam = params.get('search');

    if (pageParam !== null) {
      const urlPageIndex = parseInt(pageParam, 10) - 1;
      setPageIndex(urlPageIndex);
    }
    if (pageSizeParam !== null) {
      setPageSize(parseInt(pageSizeParam, 10));
    }
    if (searchParam !== null) {
      setSearchTerm(searchParam);
    }
  }, [location.search]);

  useEffect(() => {
    // Debounce the search term
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm?.trim());
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    updateURLWithParams(0, pageSize, value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    updateURLWithParams(0, pageSize, '');
  };

  // Save new mapping
  const handleSaveNew = () => {
    const isSourceValid = validateSourceField(newSourceField);
    if (!isSourceValid) {
      return;
    }
    const isTargetValid = validateTargetField(newTargetField);
    if (!isTargetValid) {
      return;
    }

    const newMapping = {
      source_field: newSourceField.trim(),
      target_field: newTargetField.trim()
    };

    createMappingMutation.mutate(newMapping);
    // Don't clear states here - they'll be cleared in onSuccess
  };

  // Save edited mapping
  const handleSaveEdit = () => {
    const currentMapping = mappingsData.find(m => m.id === editingId);

    const isSourceValid = validateSourceField(editedSourceField);
    const isTargetValid = validateTargetField(editedTargetField, currentMapping);

    if (!isSourceValid || !isTargetValid) {
      return;
    }

    updateSingleMappingMutation.mutate({
      id: editingId,
      mapping: {
        source_field: editedSourceField.trim(),
        target_field: editedTargetField.trim()
      }
    });
  };

  // Delete a mapping
  const handleDelete = (id) => {
    setDeleteId(id);
    setConfirmDeleteOpen(true);
  };

  // Start adding a new mapping
  const handleAddNew = () => {
    setIsAddingNew(true);
    setNewSourceField('');
    setNewTargetField('');
    setSourceFieldError('');
    setTargetFieldError('');
    setPageIndex(0);
  };

  // Cancel adding new mapping
  const handleCancelAdd = () => {
    setIsAddingNew(false);
    setNewSourceField('');
    setNewTargetField('');
    setSourceFieldError('');
    setTargetFieldError('');
  };

  // Open reset confirmation dialog
  const handleOpenResetConfirm = () => {
    setConfirmResetOpen(true);
  };

  // Close reset confirmation dialog
  const handleCloseResetConfirm = () => {
    setConfirmResetOpen(false);
  };

  // Confirm reset action
  const handleConfirmReset = () => {
    resetMappingsMutation.mutate();
  };

  // Function to confirm deletion
  const handleConfirmDelete = () => {
    setDeletingId(deleteId);
    deleteMappingMutation.mutate(deleteId, {
      onSettled: () => {
        setConfirmDeleteOpen(false);
      }
    });
  };

  // Function to refresh the field mappings
  const handleRefresh = () => {
    setIsRefreshing(true);
    refetch().then(() => {
      setTimeout(() => {
        setIsRefreshing(false);
      }, 500); // small delay to ensure UI shows loading state
    }).catch(() => {
      setIsRefreshing(false);
    });
  };

  // Start editing a mapping
  const handleEditClick = (mapping) => {
    setEditingId(mapping.id);
    setEditedSourceField(mapping.source_field);
    setEditedTargetField(mapping.target_field);
    setSourceFieldError('');
    setTargetFieldError('');
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditedSourceField('');
    setEditedTargetField('');
    setSourceFieldError('');
    setTargetFieldError('');
  };

  const handlePageChange = (newPage) => {
    setPageIndex(newPage);
    updateURLWithParams(newPage, pageSize, searchTerm);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPageIndex(0);
    updateURLWithParams(0, newPageSize, searchTerm);
  };

  // Handle sorting
  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  // Check if any mutation is loading
  const isMutating = updateMappingsMutation.isPending || updateSingleMappingMutation.isPending || createMappingMutation.isPending || deleteMappingMutation.isPending || resetMappingsMutation.isPending || isRefreshing;

  // Calculate which rows are loading (skeleton effect)
  const loadingRows = [];
  if (savingId) {
    loadingRows.push(savingId);
  }
  if (deletingId) {
    loadingRows.push(deletingId);
  }
  if (isAddingSaving) {
    loadingRows.push('new');
  }

  return (
    <Box>
      {/* Sync in progress warning banner */}
      {isSyncRunning && isAdmin && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          Customer synchronization is currently in progress. Field mapping updates are disabled until the sync completes.
        </Alert>
      )}

      <ServerDataTable
        data={paginatedData}
        columns={columns}
        sortConfig={sortConfig}
        onSort={handleSort}
        totalRows={adjustedTotalCount}
        currentPage={pageIndex}
        rowsPerPage={pageSize}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handlePageSizeChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
        loading={isLoading || isRefreshing || syncStatusLoading}
        isError={isError}
        error={error}
        noDataMessage='No field mappings found. Add a mapping or reset to default.'
        totalPages={Math.ceil(adjustedTotalCount / pageSize)}
        enableSearch={true}
        searchTerm={searchTerm}
        setSearchTerm={handleSearch}
        onClearSearch={handleClearSearch}
        searchPlaceholder='Search by Source Field, Target Field'
        searchSize='small'
        loadingRows={loadingRows}
        buttons={
          <Box display='flex' gap={2} sx={{ width: { xs: '100% !important', sm: 'auto !important' } }}>
            <Button sx={{ width: { xs: '100% !important', sm: 'auto !important' } }} variant='contained' color='primary' startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={Boolean(isLoading) || Boolean(isMutating) || Boolean(isAddingNew) || isSyncRunning || Boolean(syncStatusLoading)}>
              Refresh
            </Button>
            {isAdmin ? (
              <>
                <Button sx={{ width: { xs: '100% !important', sm: 'auto !important' } }} variant='contained' color='primary' startIcon={<AddIcon />} onClick={handleAddNew} disabled={!isAdmin || Boolean(isAddingNew) || Boolean(isMutating) || Boolean(isRefreshing) || isSyncRunning || Boolean(isLoading) || Boolean(syncStatusLoading)}>
                  Add
                </Button>
                <Button sx={{ width: { xs: '100% !important', sm: 'auto !important' } }} variant='contained' color='warning' startIcon={<RestoreIcon />} onClick={handleOpenResetConfirm} disabled={!isAdmin || Boolean(isMutating) || Boolean(isRefreshing) || Boolean(isAddingNew) || isSyncRunning || Boolean(isLoading) || Boolean(syncStatusLoading)}>
                  Reset
                </Button>
              </>
            ) : (<></>)}
          </Box>
        }
      />

      <ConfirmDialog
        open={confirmResetOpen}
        onClose={handleCloseResetConfirm}
        onCancel={handleCloseResetConfirm}
        onConfirm={handleConfirmReset}
        confirmColor='warning'
        title='Reset Field Mappings'
        message='Are you sure you want to reset all field mappings to their default values? This action cannot be undone.'
        confirmText='Reset'
        cancelText='Cancel'
        severity='warning'
        isLoading={resetMappingsMutation.isPending}
      />

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onCancel={() => setConfirmDeleteOpen(false)}
        onConfirm={handleConfirmDelete}
        confirmColor='error'
        title='Delete Field Mapping'
        message='Are you sure you want to delete this field mapping? This action cannot be undone.'
        confirmText='Delete'
        cancelText='Cancel'
        severity='warning'
        isLoading={Boolean(deletingId)}
      />
    </Box>
  );
}
